import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DISPATCH_EPS } from "@/features/dispatch/constants";
import type { DispatchFormValues } from "@/features/dispatch/schema";
import type {
  DispatchableFulfillment,
  DispatchNote,
  DispatchNoteLine,
  DispatchNoteListRow,
  DispatchStatus,
} from "@/features/dispatch/types";
import { fiscalYearLabel } from "@/features/purchase/constants";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const text = (value: string | undefined) => value?.trim() || null;

function money(v: unknown) {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

async function nextNoteNumber(dispatchDate: string): Promise<string> {
  const { data: company } = await supabase
    .from("company")
    .select("dispatch_prefix, fiscal_year_start_month")
    .limit(1)
    .maybeSingle();
  const prefix =
    ((company?.dispatch_prefix as string | undefined) ?? "DN").trim().toUpperCase() || "DN";
  const fy = fiscalYearLabel(
    dispatchDate,
    Number(company?.fiscal_year_start_month) || 4,
  );
  const pattern = `${prefix}-${fy}-`;
  const { data, error } = await supabase
    .from("dispatch_notes")
    .select("note_number")
    .ilike("note_number", `${pattern}%`)
    .order("note_number", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.note_number as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  return `${pattern}${String((Number.isNaN(n) ? 0 : n) + 1).padStart(4, "0")}`;
}

async function loadDispatchedByFulfillment(soId: string) {
  const { data: notes, error: noteErr } = await supabase
    .from("dispatch_notes")
    .select("id")
    .eq("sales_order_id", soId)
    .neq("status", "cancelled");
  if (noteErr) throw noteErr;

  const map = new Map<string, { qty: number; weight: number }>();
  const noteIds = (notes ?? []).map((n) => String(n.id));
  if (!noteIds.length) return map;

  const { data, error } = await supabase
    .from("dispatch_note_lines")
    .select("quantity, weight, sales_order_fulfillment_id")
    .in("dispatch_note_id", noteIds);
  if (error) throw error;

  for (const row of data ?? []) {
    const raw = row as Record<string, unknown>;
    const fid = raw.sales_order_fulfillment_id as string | null;
    if (!fid) continue;
    const cur = map.get(fid) ?? { qty: 0, weight: 0 };
    cur.qty += money(raw.quantity);
    cur.weight += money(raw.weight);
    map.set(fid, cur);
  }
  return map;
}

export function useDispatchableFulfillments(soId?: string) {
  return useQuery({
    queryKey: ["dispatchable_fulfillments", soId],
    enabled: isSupabaseConfigured && Boolean(soId),
    queryFn: async (): Promise<DispatchableFulfillment[]> => {
      const { data, error } = await supabase
        .from("sales_order_fulfillments")
        .select(
          `id, sales_order_line_id, inventory_batch_id, quantity, weight,
          inventory_batches(batch_no),
          sales_order_lines(line_no, materials(name))`,
        )
        .eq("sales_order_id", soId!);
      if (error) throw error;

      const dispatched = await loadDispatchedByFulfillment(soId!);

      return (data ?? [])
        .map((row) => {
          const raw = row as Record<string, unknown>;
          const batch = raw.inventory_batches as { batch_no?: string } | null;
          const line = raw.sales_order_lines as {
            line_no?: number;
            materials?: { name?: string } | null;
          } | null;
          const fid = String(raw.id);
          const fQty = money(raw.quantity);
          const fWt = money(raw.weight);
          const used = dispatched.get(fid) ?? { qty: 0, weight: 0 };
          const remQty = Math.max(0, fQty - used.qty);
          const remWt = Math.max(0, fWt - used.weight);
          return {
            fulfillment_id: fid,
            sales_order_line_id: String(raw.sales_order_line_id),
            inventory_batch_id: String(raw.inventory_batch_id),
            line_no: line?.line_no ?? 0,
            material_name: line?.materials?.name ?? null,
            batch_no: batch?.batch_no ?? null,
            fulfilled_qty: fQty,
            fulfilled_weight: fWt,
            dispatched_qty: used.qty,
            dispatched_weight: used.weight,
            remaining_qty: remQty,
            remaining_weight: remWt,
          };
        })
        .filter((r) => r.remaining_weight > DISPATCH_EPS || r.remaining_qty > DISPATCH_EPS)
        .sort((a, b) => a.line_no - b.line_no);
    },
  });
}

async function replaceLines(noteId: string, values: DispatchFormValues) {
  const { error: delErr } = await supabase
    .from("dispatch_note_lines")
    .delete()
    .eq("dispatch_note_id", noteId);
  if (delErr) throw delErr;

  let totalQty = 0;
  let totalWt = 0;
  const rows = values.lines.map((line, i) => {
    totalQty += money(line.quantity);
    totalWt += money(line.weight);
    return {
      dispatch_note_id: noteId,
      line_no: i + 1,
      sales_order_line_id: line.sales_order_line_id,
      sales_order_fulfillment_id: text(line.sales_order_fulfillment_id),
      inventory_batch_id: line.inventory_batch_id,
      quantity: money(line.quantity),
      weight: money(line.weight),
      notes: text(line.notes),
    };
  });

  if (rows.length) {
    const { error } = await supabase.from("dispatch_note_lines").insert(rows);
    if (error) throw error;
  }

  return { totalQty, totalWt };
}

async function logisticsFields(values: DispatchFormValues) {
  const transporterId = text(values.transporter_id);
  const vehicleId = text(values.vehicle_id);
  const driverId = text(values.driver_id);

  let transporterName = text(values.transporter_name);
  let vehicleNumber = text(values.vehicle_number);
  let driverName = text(values.driver_name);

  if (transporterId) {
    const { data } = await supabase
      .from("transporters")
      .select("name")
      .eq("id", transporterId)
      .maybeSingle();
    if (data?.name) transporterName = String(data.name);
  }
  if (vehicleId) {
    const { data } = await supabase
      .from("vehicles")
      .select("vehicle_number")
      .eq("id", vehicleId)
      .maybeSingle();
    if (data?.vehicle_number) vehicleNumber = String(data.vehicle_number);
  }
  if (driverId) {
    const { data } = await supabase
      .from("drivers")
      .select("name")
      .eq("id", driverId)
      .maybeSingle();
    if (data?.name) driverName = String(data.name);
  }

  return {
    transporter_id: transporterId,
    vehicle_id: vehicleId,
    driver_id: driverId,
    transporter_name: transporterName,
    vehicle_number: vehicleNumber,
    driver_name: driverName,
    lr_number: text(values.lr_number),
  };
}

async function applyDispatchedBatches(noteId: string) {
  const { data: lines, error } = await supabase
    .from("dispatch_note_lines")
    .select("inventory_batch_id")
    .eq("dispatch_note_id", noteId);
  if (error) throw error;

  const batchIds = [...new Set((lines ?? []).map((l) => String(l.inventory_batch_id)))];
  const now = new Date().toISOString();
  for (const batchId of batchIds) {
    const { error: updErr } = await supabase
      .from("inventory_batches")
      .update({ batch_status: "dispatched", updated_at: now })
      .eq("id", batchId)
      .in("batch_status", ["consumed", "in_stock", "reserved"]);
    if (updErr) throw updErr;
  }
}

export function dispatchToForm(note: DispatchNote): DispatchFormValues {
  return {
    sales_order_id: note.sales_order_id,
    client_id: note.client_id,
    invoice_id: note.invoice_id ?? "",
    dispatch_date: note.dispatch_date,
    ship_to_text: note.ship_to_text ?? "",
    notes: note.notes ?? "",
    instructions: note.instructions ?? "",
    transporter_id: note.transporter_id ?? "",
    vehicle_id: note.vehicle_id ?? "",
    driver_id: note.driver_id ?? "",
    vehicle_number: note.vehicle_number ?? "",
    transporter_name: note.transporter_name ?? "",
    driver_name: note.driver_name ?? "",
    lr_number: note.lr_number ?? "",
    lines: (note.lines ?? []).map((l) => ({
      sales_order_line_id: l.sales_order_line_id,
      sales_order_fulfillment_id: l.sales_order_fulfillment_id ?? "",
      inventory_batch_id: l.inventory_batch_id,
      quantity: l.quantity,
      weight: l.weight,
      notes: l.notes ?? "",
      batch_no: l.batch_no ?? "",
      material_name: l.material_name ?? "",
      line_no: l.so_line_no ?? l.line_no,
    })),
  };
}

export function useDispatchNotes(filters?: { clientId?: string; soId?: string }) {
  return useQuery({
    queryKey: ["dispatch_notes", filters?.clientId ?? "all", filters?.soId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<DispatchNoteListRow[]> => {
      let q = supabase
        .from("dispatch_notes")
        .select(
          `*,
          clients(code, name),
          sales_orders(so_number),
          invoices(invoice_number),
          dispatch_note_lines(id)`,
        )
        .order("dispatch_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (filters?.clientId) q = q.eq("client_id", filters.clientId);
      if (filters?.soId) q = q.eq("sales_order_id", filters.soId);
      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const client = raw.clients as { code?: string; name?: string } | null;
        const so = raw.sales_orders as { so_number?: string } | null;
        const inv = raw.invoices as { invoice_number?: string } | null;
        const lines = (raw.dispatch_note_lines as { id: string }[] | null) ?? [];
        const {
          clients: _c,
          sales_orders: _s,
          invoices: _i,
          dispatch_note_lines: _l,
          ...rest
        } = raw;
        return {
          ...(rest as DispatchNote),
          total_quantity: money((rest as DispatchNote).total_quantity),
          total_weight: money((rest as DispatchNote).total_weight),
          client_code: client?.code ?? null,
          client_name: client?.name ?? null,
          so_number: so?.so_number ?? null,
          invoice_number: inv?.invoice_number ?? null,
          line_count: lines.length,
        };
      });
    },
  });
}

export function useDispatchNote(id?: string) {
  return useQuery({
    queryKey: ["dispatch_notes", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<DispatchNote> => {
      const { data, error } = await supabase
        .from("dispatch_notes")
        .select(
          `*,
          clients(code, name),
          sales_orders(so_number),
          invoices(invoice_number),
          dispatch_note_lines(
            *,
            inventory_batches(batch_no),
            sales_order_lines(line_no, materials(name))
          )`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const client = raw.clients as { code?: string; name?: string } | null;
      const so = raw.sales_orders as { so_number?: string } | null;
      const inv = raw.invoices as { invoice_number?: string } | null;
      const linesRaw = (raw.dispatch_note_lines as Record<string, unknown>[] | null) ?? [];
      const {
        clients: _c,
        sales_orders: _s,
        invoices: _i,
        dispatch_note_lines: _l,
        ...rest
      } = raw;

      const lines = linesRaw
        .map((l) => {
          const batch = l.inventory_batches as { batch_no?: string } | null;
          const sol = l.sales_order_lines as {
            line_no?: number;
            materials?: { name?: string } | null;
          } | null;
          const { inventory_batches: _b, sales_order_lines: _sl, ...lineRest } = l;
          return {
            ...(lineRest as DispatchNoteLine),
            quantity: money((lineRest as DispatchNoteLine).quantity),
            weight: money((lineRest as DispatchNoteLine).weight),
            batch_no: batch?.batch_no ?? null,
            material_name: sol?.materials?.name ?? null,
            so_line_no: sol?.line_no ?? null,
          };
        })
        .sort((a, b) => a.line_no - b.line_no);

      return {
        ...(rest as DispatchNote),
        total_quantity: money((rest as DispatchNote).total_quantity),
        total_weight: money((rest as DispatchNote).total_weight),
        client_code: client?.code ?? null,
        client_name: client?.name ?? null,
        so_number: so?.so_number ?? null,
        invoice_number: inv?.invoice_number ?? null,
        lines,
      };
    },
  });
}

export function useCreateDispatchNote() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: DispatchFormValues) => {
      const noteNumber = await nextNoteNumber(values.dispatch_date);
      const logistics = await logisticsFields(values);
      const { data, error } = await supabase
        .from("dispatch_notes")
        .insert({
          note_number: noteNumber,
          client_id: values.client_id,
          sales_order_id: values.sales_order_id,
          invoice_id: text(values.invoice_id),
          dispatch_date: values.dispatch_date,
          status: "draft",
          ship_to_text: text(values.ship_to_text),
          notes: text(values.notes),
          instructions: text(values.instructions),
          ...logistics,
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;

      const totals = await replaceLines(data.id, values);
      const { data: updated, error: updErr } = await supabase
        .from("dispatch_notes")
        .update({
          total_quantity: totals.totalQty,
          total_weight: totals.totalWt,
          updated_at: new Date().toISOString(),
        })
        .eq("id", data.id)
        .select("*")
        .single();
      if (updErr) throw updErr;
      return updated as DispatchNote;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dispatch_notes"] });
      await queryClient.invalidateQueries({ queryKey: ["dispatchable_fulfillments"] });
      toast.success("Dispatch note created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create dispatch"),
  });
}

export function useUpdateDispatchNote() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: DispatchFormValues }) => {
      const { data: existing, error: exErr } = await supabase
        .from("dispatch_notes")
        .select("status")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      if (!["draft", "loading"].includes(existing.status as string)) {
        throw new Error("Only draft or loading notes can be edited");
      }

      const totals = await replaceLines(id, values);
      const logistics = await logisticsFields(values);
      const { data, error } = await supabase
        .from("dispatch_notes")
        .update({
          client_id: values.client_id,
          sales_order_id: values.sales_order_id,
          invoice_id: text(values.invoice_id),
          dispatch_date: values.dispatch_date,
          ship_to_text: text(values.ship_to_text),
          notes: text(values.notes),
          instructions: text(values.instructions),
          ...logistics,
          total_quantity: totals.totalQty,
          total_weight: totals.totalWt,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as DispatchNote;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["dispatch_notes"] });
      await queryClient.invalidateQueries({ queryKey: ["dispatch_notes", vars.id] });
      await queryClient.invalidateQueries({ queryKey: ["dispatchable_fulfillments"] });
      toast.success("Dispatch note saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save dispatch"),
  });
}

export function useUpdateDispatchStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: DispatchStatus }) => {
      const { data: existing, error: exErr } = await supabase
        .from("dispatch_notes")
        .select("status")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;

      if (status === "dispatched" && existing.status === "dispatched") {
        throw new Error("Already dispatched");
      }
      if (status === "cancelled" && existing.status === "dispatched") {
        throw new Error("Cannot cancel a dispatched note");
      }

      if (status === "dispatched") {
        await applyDispatchedBatches(id);
      }

      const { data, error } = await supabase
        .from("dispatch_notes")
        .update({
          status,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as DispatchNote;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["dispatch_notes"] });
      await queryClient.invalidateQueries({ queryKey: ["dispatch_notes", data.id] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches"] });
      await queryClient.invalidateQueries({ queryKey: ["dispatchable_fulfillments"] });
      toast.success(`Dispatch ${data.status}`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function useDeleteDispatchNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: existing, error: exErr } = await supabase
        .from("dispatch_notes")
        .select("status")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      if (existing.status !== "draft") throw new Error("Only draft notes can be deleted");
      const { error } = await supabase.from("dispatch_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dispatch_notes"] });
      await queryClient.invalidateQueries({ queryKey: ["dispatchable_fulfillments"] });
      toast.success("Dispatch note deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete"),
  });
}
